/**
 * Product card section: images, quick shop and color swatches
 * ------------------------------------------------
 * sections/product-card.liquid is fetched through the Section Rendering API
 * and inserted after page load (today by Nosto's <nosto-dynamic-card>), so
 * the theme code that sets up cards once on load never sees these cards.
 * Scoped to cards inside that section's wrapper (.product-card-section);
 * <nosto-dynamic-card> is also matched in case Nosto drops the wrapper.
 * If nothing inserts the section, this script does nothing.
 *
 * Images: theme.css keeps .image-element at opacity 0 until AOS adds
 * .aos-animate, and AOS runs with disableMutationObserver, so it never
 * reaches inserted cards. When a MutationObserver sees new cards it calls
 * AOS.refreshHard(), as theme.reinitProductGridItem() does after native
 * recommendations load, so images fade in as they scroll into view. Inserts
 * are batched to one refresh per frame. Cards inside <same-shelf-scroller>
 * are skipped: the shelf refreshes AOS itself (syncAos()).
 *
 * Quick shop: the first hover/focus on a card (desktop only, as in
 * theme.initQuickShop() in assets/theme.js) preloads its quick shop modal
 * with theme.preloadProductModal(), which also wires the card's button. The
 * card's modal (snippets/quick-shop-modal.liquid) is moved to <body> first:
 * theme.Modals opens it in place, and a position: fixed modal inside
 * a carousel (Nosto's today) would be clipped or offset by its own
 * overflow/transform. The same product is often also in a native grid on
 * the page, with the same QuickShopModal-{id} and QuickShopHolder-{handle}
 * ids, and the theme looks both up with getElementById. So the modal gets
 * its own id and the button its own open class (passed to the theme as
 * the "product id"), and the modal goes first in <body> so the holder
 * lookup finds this card's copy; the holder's id is dropped right after so
 * later lookups for native cards skip it. A modal left in <body> by a card
 * that has since been removed is removed with it. Opening the modal closes
 * the cart drawer, as it does for native cards (theme.Drawers listens for
 * modalOpen).
 *
 * Color swatches: hovering or touching a swatch shows that color's image and
 * points the card link at the variant, the same as ProductRecommendations'
 * colorSwatchHovering() in assets/theme.js, but per card.
 */
(function () {
  var CARD = '.product-card-section .grid-product, nosto-dynamic-card .grid-product';

  function closestCard(el) {
    return el && el.closest ? el.closest(CARD) : null;
  }

  var refreshQueued = false;

  function refreshAnimations() {
    if (refreshQueued) return;
    refreshQueued = true;
    requestAnimationFrame(function () {
      refreshQueued = false;
      if (window.AOS) AOS.refreshHard();
    });
  }

  function needsRefresh(card) {
    return !card.closest('same-shelf-scroller');
  }

  function hasCard(node) {
    if (node.nodeType !== 1) return false;
    if (node.matches(CARD)) return needsRefresh(node);
    return Array.prototype.some.call(node.querySelectorAll(CARD), needsRefresh);
  }

  var quickShopCount = 0;
  var movedModals = new Map();

  function preloadQuickShop(evt) {
    if (!window.theme || !theme.settings.quickView || theme.config.bpSmall) return;

    var card = closestCard(evt.target);
    if (!card || card.dataset.quickShopLoaded || !card.dataset.productId) return;

    var modal = card.querySelector('.modal--quick-shop');
    var btn = card.querySelector('.quick-product__btn');
    if (!modal || !btn) return;

    card.dataset.quickShopLoaded = 'true';

    movedModals.forEach(function (owner, moved) {
      if (owner.isConnected) return;
      moved.remove();
      movedModals.delete(moved);
    });

    var id = card.dataset.productId + '-card-' + ++quickShopCount;
    modal.id = 'QuickShopModal-' + id;
    btn.classList.remove('js-modal-open-quick-modal-' + card.dataset.productId);
    btn.classList.add('js-modal-open-quick-modal-' + id);
    document.body.prepend(modal);
    movedModals.set(modal, card);

    theme.preloadProductModal(card.dataset.productHandle, id, btn);

    // The theme has looked the holder up by now; a native card of the same
    // product must find its own.
    var holder = modal.querySelector('[id^="QuickShopHolder-"]');
    if (holder) holder.removeAttribute('id');
  }

  function swatchFrom(evt) {
    var swatch = evt.target.closest && evt.target.closest('.color-swatch--with-image');
    return swatch && closestCard(swatch) ? swatch : null;
  }

  function showSwatchImage(evt) {
    var swatch = swatchFrom(evt);
    if (!swatch || swatch.contains(evt.relatedTarget)) return;

    var card = closestCard(swatch);
    card.querySelectorAll('.grid-product__color-image.is-active, .color-swatch--with-image.is-active').forEach(function (el) {
      el.classList.remove('is-active');
    });

    var image = card.querySelector('.grid-product__color-image--' + swatch.dataset.variantId);
    if (image) {
      image.style.backgroundImage = 'url(' + swatch.dataset.variantImage + ')';
      image.classList.add('is-active');
    }
    swatch.classList.add('is-active');

    var link = swatch.closest('.grid-item__link');
    if (link && swatch.dataset.url) link.setAttribute('href', swatch.dataset.url);
  }

  function hideSwatchImage(evt) {
    var swatch = swatchFrom(evt);
    if (!swatch || swatch.contains(evt.relatedTarget)) return;

    var image = closestCard(swatch).querySelector('.grid-product__color-image--' + swatch.dataset.variantId);
    if (image) image.classList.remove('is-active');
  }

  // Whatever inserts the cards may run first, so some may already be in the page.
  if (hasCard(document.documentElement)) refreshAnimations();
  new MutationObserver(function (mutations) {
    var added = mutations.some(function (mutation) {
      return Array.prototype.some.call(mutation.addedNodes, hasCard);
    });
    if (added) refreshAnimations();
  }).observe(document.documentElement, { childList: true, subtree: true });

  document.addEventListener('mouseover', preloadQuickShop);
  document.addEventListener('focusin', preloadQuickShop);
  document.addEventListener('mouseover', showSwatchImage);
  document.addEventListener('touchstart', showSwatchImage, { passive: true });
  document.addEventListener('mouseout', hideSwatchImage);
})();
